import { arrayMove } from '@dnd-kit/sortable';
import { AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { OnDragEndParams } from '../../components/DragAndDrop/dragAndDrop.types';
import LangEnum from '../../lang/LangEnum';
import { IMedia, IProductMedia } from '../../lib/MediasLib';
import { TeceoApiResponse } from '../../lib/basicRepository';
import { uploadFiles } from '../../lib/uploadImage';
import { restApi } from '../../services/api';
import snackbarStore from '../../stores/Snackbar.store';
import { useTranslation } from '../translation/TranslationContext';
import { MediaType, getMediaType } from './mimeType';

const setPosition = (parent: any, index: number) => ({ ...parent, position: index });

type TagVariables = 'NORMAL' | 'NORMAL' | 'CHANGED' | 'LOADING' | 'SUCCESS' | 'ERROR';

type SavedMedia = {
  id: string;
  url: string;
  position: number;
  type: MediaType;
  processed: boolean;
  errorMessage: string;
};

type MediaListManagerStatus = {
  tag: TagVariables;
  medias: IMedia[];
  remove: any[];
  message: string;
};

const initialState: MediaListManagerStatus = {
  tag: 'NORMAL',
  medias: [],
  remove: [],
  message: '',
};

const getMediasServer = (payload: IMedia) => ({ type: 'GET_MEDIAS', payload });
const handleSortAction = (payload: OnDragEndParams) => ({ type: 'HANDLE_SORT', payload });
const addMedias = (payload: IMedia[]) => ({ type: 'ADD_MEDIAS', payload });
const saveMedias = () => ({ type: 'SAVE' });
const removeMedia = (payload: IMedia) => ({ type: 'REMOVE', payload });
const saveMediasResult = (payload: any) => ({ type: 'SAVE_RESULT', payload });

export const MAX_VIDEO_SIZE = 5;
export const MAX_IMAGE_SIZE = 5;
export const MAX_PDF_SIZE = 100;
export const MAX_FILE_SIZE = 10;
export const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE * 1024 * 1024;

const DEFAULT_FILE_SIZES: Record<MediaType, number> = {
  PDF: MAX_PDF_SIZE,
  VIDEO: MAX_VIDEO_SIZE,
  IMAGE: MAX_IMAGE_SIZE,
  FILE: MAX_FILE_SIZE,
};

const isValidSize = (mediaWithFile: IMedia): boolean => {
  if (mediaWithFile?.file) {
    const mediaType = getMediaType(mediaWithFile?.file) || 'IMAGE';
    return mediaWithFile?.file.size <= DEFAULT_FILE_SIZES[mediaType] * 1024 * 1024;
  }
  return false;
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SAVE':
      return { ...state, status: 'LOADING' };
    case 'SAVE_RESULT':
      return {
        ...state,
        status: 'NORMAL',
        remove: [],
      };
    case 'REMOVE':
      return {
        ...state,
        status: 'CHANGED',
        remove: action.payload.id ? state.remove.concat(action.payload) : state.remove,
        medias: state.medias
          .filter((p: any) => {
            if (!action.payload.id) {
              return p.id || +p.position !== +action.payload.position;
            }
            return p.id !== action.payload.id;
          })
          .map(setPosition),
      };
    case 'ADD_MEDIAS':
      const newMedias = action.payload.map((media: IProductMedia) => ({
        ...media,
        id: media.id || uuidv4(),
        type: getMediaType(media?.file) || 'IMAGE',
      }));
      const finalMedias = newMedias.concat(state.medias).map(setPosition);
      return { ...state, status: 'CHANGED', medias: finalMedias };
    case 'GET_MEDIAS':
      if (action.payload.ok) {
        if (action.payload?.data?.length == null) {
          return { ...state, status: 'NORMAL' };
        }
        return { ...state, medias: action.payload.data, status: 'NORMAL' };
      }
      return { ...state, message: action.payload.data?.message, status: 'ERROR' };

    case 'HANDLE_SORT':
      const { oldIndex, newIndex } = action.payload;
      return {
        ...state,
        medias: arrayMove(state.medias, oldIndex, newIndex).map(setPosition),
        status: 'CHANGED',
      };
    default:
      return state;
  }
};

export type MediaApi = {
  list: (options?: any) => any;
  remove: (data: any, options?: any) => any;
};

export type AutoSaveMediaApi = MediaApi & {
  create: (data: any, axiosConfig?: AxiosRequestConfig) => any;
  update: (data: any, axiosConfig?: AxiosRequestConfig) => any;
};

type ManagerType =
  | { id?: string; api: AutoSaveMediaApi; autoSave: true }
  | { id?: string; api: MediaApi; autoSave?: false };

export type MediaListManager = {
  id: string;
  onAdd: (medias: IMedia[]) => void;
  onSave: (productId: string, productColorId: string) => Promise<void>;
  onChange: (index: number, change: any) => void;
  load: () => Promise<void>;
  loading: boolean;
  selectMode: boolean;
  onSelectClick: () => void;
  onRemoveClick: (media: any) => Promise<void>;
  onSortEnd: (data: any) => void;
} & MediaListManagerStatus;

const useMediaListManager = (params: ManagerType): MediaListManager => {
  const { api, autoSave = false, id = 'manyImage' } = params;
  const autoSaveApi = params.autoSave ? params.api : undefined;
  const [selectMode, setSelectMode] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { handleMessage } = snackbarStore;
  const [loading, setLoading] = useState(false);
  const { translate } = useTranslation();

  const saveMedia = useCallback(
    async (media: any) => {
      if (!autoSaveApi) {
        return { ok: false };
      }
      const data = {
        ...media,
        position: media.position || 0,
        ...(media.image && { file: media.image.file }),
      };
      if (!media.id) {
        delete data.coverUrl;
      }

      return media.id ? autoSaveApi.update(data) : autoSaveApi.create(data);
    },
    [autoSaveApi],
  );

  const saveProductColorMedias = useCallback(
    async (medias: IMedia[], productId: string, productColorId: string) => {
      try {
        if (!autoSave) {
          setLoading(true);
        }

        const data = await Promise.all(
          medias.map(async (media) =>
            media.url
              ? { id: media.id, position: media.position, type: media.type, url: media.url }
              : {
                  position: media.position,
                  type: media.type,
                  url: (await uploadFiles(Array(media.file!))).data.url,
                },
          ),
        );

        const response: TeceoApiResponse<SavedMedia[]> = await restApi.patch(
          `products/${productId}/colors/${productColorId}/medias`,
          data,
        );
        if (!autoSave) {
          setLoading(false);
        }
        return response;
      } catch (error) {
        return { ok: false, data: error } as TeceoApiResponse<SavedMedia[]>;
      }
    },
    [autoSave],
  );

  const getMediasFromServer = useCallback(async () => {
    setLoading(true);
    const response = await api.list();
    setLoading(false);
    dispatch(getMediasServer({ ...response, data: response.data.data }));
  }, []);

  const handleSave = useCallback(
    async (productId: string, productColorId: string) => {
      dispatch(saveMedias());
      const responseList: TeceoApiResponse<SavedMedia[]> = await saveProductColorMedias(
        state.medias,
        productId,
        productColorId,
      );
      await Promise.all(state.remove.map(api.remove));
      if (responseList?.ok) {
        dispatch(saveMediasResult(responseList));
        handleMessage({
          type: 'success',
          message: translate(LangEnum.SNACKBAR_MEDIA_SUCCESSFULLY_SAVED),
        });
      } else {
        handleMessage({
          type: 'error',
          message: translate(LangEnum.SNACKBAR_ERROR_SAVING_MEDIA),
        });
        getMediasFromServer();
      }
    },
    [
      api.remove,
      getMediasFromServer,
      handleMessage,
      saveProductColorMedias,
      state.medias,
      state.remove,
      translate,
    ],
  );

  const handleAdd = async (medias: IMedia[]) => {
    const invalidMedias = medias.filter((media: IMedia) => !isValidSize(media));
    if (invalidMedias.length > 0) {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.SNACKBAR_INVALID_FILE_SIZE, {
          MAX_IMAGE_SIZE,
          MAX_VIDEO_SIZE,
          MAX_PDF_SIZE,
        }),
      });
    }
    const validMedias = medias.filter((media: IMedia) => isValidSize(media));

    if (autoSave) {
      validMedias.forEach((media) =>
        saveMedia(media).then((response) => {
          if (response.ok) {
            dispatch(addMedias([response.data]));
          } else {
            handleMessage({
              type: 'error',
              message: translate(LangEnum.SNACKBAR_ERROR_SAVING_MEDIA),
            });
          }
        }),
      );
      return;
    }

    dispatch(addMedias(validMedias));
  };

  const handleRemove = async (media: IMedia) => {
    dispatch(removeMedia(media));
    if (autoSave) {
      api.remove(media);
    }
  };

  const handleSort = (sort: OnDragEndParams) => {
    dispatch(handleSortAction(sort));
    if (autoSave) {
      const media = setPosition(state.medias[sort.oldIndex], sort.newIndex);
      saveMedia(media);
    }
  };

  const handleSelectMode = () => {
    setSelectMode(!selectMode);
  };

  useEffect(() => {
    getMediasFromServer();
  }, [getMediasFromServer]);

  return {
    ...state,
    id,
    onAdd: handleAdd,
    onSave: handleSave,
    load: getMediasFromServer,
    loading,
    selectMode,
    onSelectClick: handleSelectMode,
    onRemoveClick: handleRemove,
    onSortEnd: handleSort,
  };
};

export default useMediaListManager;
